import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, PenLine, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IDS } from "@/constants/testIds";

function ScoreBar({ label_cn, label_en, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-bold text-slate-600">
          <span className="cn mr-1">{label_cn}</span>· {label_en}
        </span>
        <span className="font-black text-slate-900">{value}</span>
      </div>
      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-sky-400 to-emerald-400"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
}

export default function Writing() {
  const { currentProfile, isAuthed } = useAuth();
  const level = currentProfile?.level || "A2";

  const [prompt, setPrompt] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  const loadPrompt = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/questions", {
        params: { skill: "writing", level, limit: 1 },
      });
      setPrompt(data[0] || null);
      setText("");
      setFeedback(null);
    } catch {
      toast.error("Could not load writing prompt.");
    } finally { setLoading(false); }
  }, [level]);

  useEffect(() => { loadPrompt(); }, [loadPrompt]);

  const submit = async () => {
    if (!prompt || !text.trim()) return;
    if (!isAuthed) {
      toast.info("Sign up to save your writing! 注册后可保存你的作品。");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/writing/submit", {
        prompt_id: prompt.id,
        prompt_en: prompt.prompt_en,
        text,
        level: prompt.level,
      });
      setFeedback(res.data.feedback);
      toast.success("Feedback ready! 评语已生成。");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Feedback failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div data-testid={IDS.writing.root} className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-100 to-fuchsia-50 grid place-items-center">
            <PenLine className="w-5 h-5 text-fuchsia-600" />
          </span>
          <div>
            <span className="badge-soft mb-1">{level}</span>
            <BilingualText cn="写作练习" en="Writing Practice" size="hero" />
          </div>
        </div>

        {loading ? (
          <div className="card-plush p-10 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !prompt ? (
          <div className="card-plush p-10 text-center text-slate-400">No prompt available.</div>
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-plush p-6 sm:p-8 space-y-6"
          >
            <div className="bg-fuchsia-50/50 border border-fuchsia-100 rounded-2xl p-5">
              <BilingualText
                cn={prompt.prompt_cn}
                en={prompt.prompt_en}
                size="md"
              />
              {(prompt.min_words || prompt.max_words) && (
                <p className="mt-3 text-xs text-fuchsia-800 font-bold">
                  <span className="cn mr-1">字数要求</span>· Words:{" "}
                  {prompt.min_words}–{prompt.max_words}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-600">
                <span className="cn mr-1">你的作品</span>· Your writing
              </label>
              <Textarea
                data-testid={IDS.writing.textarea}
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Start writing here… 在这里开始写作…"
                className="rounded-2xl border-slate-200 focus-visible:ring-fuchsia-400 min-h-[240px]"
                disabled={!!feedback || submitting}
              />
              <div className="text-right text-xs font-bold text-slate-500">
                {wordCount} words · {wordCount} 词
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {!feedback ? (
                <Button
                  size="lg"
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                  onClick={submit}
                  disabled={submitting || !text.trim() || !isAuthed}
                  data-testid={IDS.writing.submit}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2 text-amber-300" />
                  )}
                  <span className="cn mr-2">获取 AI 评语</span>Get AI feedback
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full font-bold"
                  onClick={loadPrompt}
                >
                  <span className="cn mr-2">下一题</span>Try another prompt →
                </Button>
              )}
              {!isAuthed && (
                <span className="text-xs text-slate-500 self-center">
                  <span className="cn">注册后可保存作品与评语。</span>{" "}
                  Sign up to save writing.
                </span>
              )}
            </div>

            {feedback && (
              <motion.div
                data-testid={IDS.writing.feedback}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-6 bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 space-y-5"
              >
                <div className="flex items-center justify-between">
                  <BilingualText cn="AI 评语" en="AI Feedback" size="md" />
                  <div className="text-right">
                    <div className="serif text-4xl font-black text-slate-900">
                      {feedback.overall ?? 0}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">
                      overall / 100
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <ScoreBar label_cn="语法" label_en="Grammar" value={feedback.grammar ?? 0} />
                  <ScoreBar label_cn="词汇" label_en="Vocabulary" value={feedback.vocabulary ?? 0} />
                  <ScoreBar label_cn="结构" label_en="Structure" value={feedback.structure ?? 0} />
                  <ScoreBar label_cn="任务完成" label_en="Task" value={feedback.task ?? 0} />
                </div>

                <div>
                  <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                    Feedback · 评语
                  </div>
                  <div className="cn text-slate-900 leading-relaxed">
                    {feedback.feedback_cn}
                  </div>
                  <div className="mt-2 text-slate-700 leading-relaxed">
                    {feedback.feedback_en}
                  </div>
                </div>

                {(feedback.rewrite_en || feedback.rewrite_cn) && (
                  <div className="pt-4 border-t border-white/60">
                    <div className="text-xs font-black text-amber-700 uppercase tracking-widest mb-2">
                      Suggested rewrite · 建议改写
                    </div>
                    <div className="cn text-sm text-slate-800">{feedback.rewrite_cn}</div>
                    <div className="text-sm text-slate-600 italic">
                      &ldquo;{feedback.rewrite_en}&rdquo;
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.section>
        )}
      </div>
    </AppLayout>
  );
}
